import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { EmailTemplate } from '../models/email-template';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class EmailsTemplatesService extends CrudService {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<EmailTemplate>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        if (context === null) {
            return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<EmailTemplate>(EmailTemplate, x);
                    return res;
                })
            );
        } else {
            return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${context.value}/emailtemplates`, httpOptions).pipe(
                map((x) => {
                    var res = Results.getCopyFrom<EmailTemplate>(EmailTemplate, x);
                    return res;
                })
            );
        }
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    update(emailTemplateId: number, emailTemplate: EmailTemplate, global: boolean): Observable<EmailTemplate> {
        let other: any = emailTemplate;

        const formData = new FormData();

        if (emailTemplate.template?.attachments) {
            emailTemplate.template.attachments.forEach((value, key) => {
                formData.append('file', value);
            });
        }

        this.objectToFormData(emailTemplate, formData);

        let url: string;

        if (global === true) {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates/${emailTemplateId}`;
        } else {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/emailtemplates/${emailTemplateId}`;
        }

        // Appel de la Web Api
        return this.http.put<EmailTemplate>(url, formData).pipe(
            map((x) => {
                return EmailTemplate.from(x);
            })
        );
    }

    delete(emailTemplateId: number, global: boolean): Observable<boolean> {
        let url: string;

        if (global === true) {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates/${emailTemplateId}`;
        } else {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/emailtemplates/${emailTemplateId}`;
        }

        // Appel de la Web Api
        return this.http.delete<EmailTemplate>(url, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return true;
            })
        );
    }

    add(emailTemplate: EmailTemplate, global: boolean): Observable<EmailTemplate> {
        let other: any = emailTemplate;

        const formData = new FormData();

        if (emailTemplate.template?.attachments) {
            emailTemplate.template.attachments.forEach((value, key) => {
                formData.append('file', value);
            });
        }

        this.objectToFormData(emailTemplate, formData);

        let url: string;

        if (global === true) {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates`;
        } else {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/emailtemplates`;
        }

        // Appel de la Web Api
        return this.http.post<EmailTemplate>(url, formData).pipe(
            map((x) => {
                return EmailTemplate.from(x);
            })
        );
    }

    get(emailTemplateId: number): Observable<EmailTemplate> {
        // Appel de la Web Api
        return this.http.get<EmailTemplate>(this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates/${emailTemplateId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return EmailTemplate.from(x);
            })
        );
    }

    deleteAttachment(emailTemplateId: number, attachmentId: number, global: boolean): Observable<EmailTemplate> {
        let url: string;

        if (global === true) {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/emailtemplates/${emailTemplateId}/attachments/${attachmentId}`;
        } else {
            url = this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/emailtemplates/${emailTemplateId}/attachments/${attachmentId}`;
        }

        // Appel de la Web Api
        return this.http.delete<EmailTemplate>(url, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return EmailTemplate.from(x);
            })
        );
    }

    private objectToFormData(object: any, formData: FormData, dataName: string = '') {
        Object.keys(object).forEach((x) => {
            if (object[x] && !(object[x] instanceof Array) && !(object[x] instanceof Object)) {
                formData.append(dataName !== '' ? `${dataName}.${x}` : x, object[x]);
            } else if (object[x] && object[x] instanceof Array && (object[x] as Array<string>).length > 0) {
                (object[x] as Array<string>).forEach((item) => {
                    formData.append(dataName !== '' ? `${dataName}.${x}` : x, item);
                });
            } else if (object[x] && object[x] instanceof Object) {
                this.objectToFormData(object[x], formData, x);
            }
        });
    }
}
